import { useEffect, useRef, useState } from "react"
import classNames from "classnames"

const MAX_SIDE = 1600 // keeps uploads well under the API's body limit
const MAX_ZOOM = 3

// Reads a chosen file into a downscaled JPEG data URL.
export const prepareImage = (file) =>
  new Promise((resolve, reject) => {
    if (!file || !/^image\//.test(file.type || "")) {
      reject(new Error("That file isn’t an image. Try a JPG or PNG."))
      return
    }
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      const scale = Math.min(1, MAX_SIDE / Math.max(img.naturalWidth, img.naturalHeight))
      const width = Math.max(1, Math.round(img.naturalWidth * scale))
      const height = Math.max(1, Math.round(img.naturalHeight * scale))
      const canvas = document.createElement("canvas")
      canvas.width = width
      canvas.height = height
      canvas.getContext("2d").drawImage(img, 0, 0, width, height)
      URL.revokeObjectURL(url)
      resolve({ src: canvas.toDataURL("image/jpeg", 0.9), width, height })
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error("We couldn’t read that image. Try a JPG or PNG."))
    }
    img.src = url
  })

// Reads a default avatar into a JPEG data URL. The PNGs have transparent
// corners, which turn black when re-encoded as JPEG, so they go on white.
export const avatarDataUrl = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      const canvas = document.createElement("canvas")
      canvas.width = img.naturalWidth
      canvas.height = img.naturalHeight
      const ctx = canvas.getContext("2d")
      ctx.fillStyle = "#fff"
      ctx.fillRect(0, 0, canvas.width, canvas.height)
      ctx.drawImage(img, 0, 0)
      resolve(canvas.toDataURL("image/jpeg", 0.92))
    }
    img.onerror = () => reject(new Error("We couldn’t load that avatar. Try another one."))
    img.src = src
  })

const clamp =(n, min, max) => Math.min(max, Math.max(min, n))

// The crop the API expects: the visible square as fractions of the image.
export const cropFractions = (c) => {
  const side = c.viewport / c.scale
  return {
    x: -c.x / c.scale / c.width,
    y: -c.y / c.scale / c.height,
    width: side / c.width,
    height: side / c.height,
  }
}

// Circular framing: drag (or arrow keys) to move, slider to zoom. Reports
// { src, width, height, viewport, scale, x, y } so the card can mirror it.
const PhotoCropper = ({ image, onChange }) => {
  const box = useRef(null)
  const drag = useRef(null)
  const [viewport, setViewport] = useState(220)
  const [zoom, setZoom] = useState(1)
  const [offset, setOffset] = useState(null)
  const [dragging, setDragging] = useState(false)

  const scaleFor = (z, v = viewport) => (v / Math.min(image.width, image.height)) * z
  const fit = (x, y, s, v = viewport) => ({
    x: clamp(x, v - image.width * s, 0),
    y: clamp(y, v - image.height * s, 0),
  })

  // measure the circle (it shrinks on small screens) and center the image
  useEffect(() => {
    const v = (box.current && box.current.clientWidth) || 220
    const s = scaleFor(1, v)
    setViewport(v)
    setZoom(1)
    setOffset({ x: (v - image.width * s) / 2, y: (v - image.height * s) / 2 })
  }, [image.src])

  useEffect(() => {
    if (offset) {
      onChange({ ...image, viewport, scale: scaleFor(zoom), x: offset.x, y: offset.y })
    }
  }, [offset, zoom, viewport])

  const onZoom = (e) => {
    const z = Number(e.target.value) || 1
    const before = scaleFor(zoom)
    const after = scaleFor(z)
    const c = viewport / 2
    // keep the center of the circle on the same point of the photo
    setOffset(fit(c - (c - offset.x) * (after / before), c - (c - offset.y) * (after / before), after))
    setZoom(z)
  }

  const onPointerDown = (e) => {
    drag.current = { px: e.clientX, py: e.clientY, x: offset.x, y: offset.y }
    e.currentTarget.setPointerCapture(e.pointerId)
    setDragging(true)
  }
  const onPointerMove = (e) => {
    if (!drag.current) return
    const d = drag.current
    setOffset(fit(d.x + e.clientX - d.px, d.y + e.clientY - d.py, scaleFor(zoom)))
  }
  const endDrag = () => {
    drag.current = null
    setDragging(false)
  }
  const onKeyDown = (e) => {
    const step = 12
    const moves = { ArrowLeft: [step, 0], ArrowRight: [-step, 0], ArrowUp: [0, step], ArrowDown: [0, -step] }
    if (!moves[e.key]) return
    e.preventDefault()
    setOffset(fit(offset.x + moves[e.key][0], offset.y + moves[e.key][1], scaleFor(zoom)))
  }

  const s = scaleFor(zoom)
  return (
    <>
      <div
        ref={box}
        className={classNames("ob-crop", { "ob-dragging": dragging })}
        role="img"
        aria-label="Your photo. Drag or use the arrow keys to move it."
        tabIndex={0}
        onPointerDown={offset ? onPointerDown : undefined}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onKeyDown={offset ? onKeyDown : undefined}
      >
        {offset && (
          <img
            src={image.src}
            alt=""
            draggable={false}
            style={{
              width: image.width * s,
              height: image.height * s,
              transform: `translate(${offset.x}px, ${offset.y}px)`,
            }}
          />
        )}
      </div>
      <div className="ob-zoom">
        <label className="ob-label" htmlFor="ob-zoom">
          Zoom
        </label>
        <input id="ob-zoom" type="range" min="1" max={MAX_ZOOM} step="0.01" value={zoom} onChange={onZoom} />
        <p className="ob-crop-hint">Drag the photo to frame your face.</p>
      </div>
    </>
  )
}

export default PhotoCropper
